<template>

    <div>

      <div class="row mt-1 mb-3 ml-2">

        <div class="col">

          <b-button variant="outline-primary"
                    @click="showX = !showX"
                    size="sm"
                    :pressed="showX"
                    v-b-tooltip.hover
                    :title="showX ? $t('message.sensor_hide_x') : $t('message.sensor_show_x')">
            <b-icon icon="calendar-date" />
          </b-button>

        </div>

      </div>

      <div class="sensorResultChart" v-if="chartdata">
         <horizontal-bar-chart
                 v-if="loaded"
                 :chartdata="chartdata"
                 v-on:set-chart="setChart"
                 :options="options"
                 :plugins="chartPlugins"
         />
      </div>

      <!-- Legende -->
      <div class="legende mt-2 ml-3">
        <ul v-bind:style="{ listStyleImage: 'url(' + listImageGruen + ')', marginBottom: 0 }"
            style="float: left">
          <li style="font-size: 0.9rem">{{ $t('message.sensor_tuer_geschlossen') }}</li>
        </ul>
        <ul v-bind:style="{ listStyleImage: 'url(' + listImageGelb + ')' }"
            style="float: left">
          <li style="font-size: 0.9rem">{{ $t('message.sensor_tuer_offen_toleranz') }}</li>
        </ul>
        <ul v-bind:style="{ listStyleImage: 'url(' + listImageRot + ')' }"
            style="float: left">
          <li style="font-size: 0.9rem">{{ $t('message.sensor_tuer_offen_schwellwert') }}</li>
        </ul>
      </div>

      <div v-if="!loaded" class="text-center">
        {{ $t('message.default_wird_geladen') }}<br>
        <SpinnerImage />
      </div>

      <b-alert show v-if="setNoData">{{ $t('message.charts_nicht_genuegend_ergebnisse') }}</b-alert>

    </div>

</template>

<script>

    import axios from 'axios'
    axios.defaults.withCredentials = true
    import HorizontalBarChart from '@/components/charts/HorizontalBarChart.vue'
    import SpinnerImage from '@/components/layout/SpinnerImage'
    import moment from "moment";

    /** Farbe je Zustand: 1=geschlossen(GRUEN), 0=geoeffnet innerhalb Toleranz(GELB), 2=geoeffnet ueber Schwellwert(ROT) */
    const STATE_COLORS = { 1: '#28a745', 0: '#ffc107', 2: '#dc3545' }
    const NO_DATA_COLOR = '#adb5bd'
    /** alte Backend-Werte (vor dem resultValue-Fix) waehrend der Rollout-Uebergangsphase abfedern */
    const LEGACY_VALUE_MAP = { '-1': 0 }

    /**
     * fasst die pro-Minute Rohdaten (labels als 'DD.MM.YYYY HH:mm', data als 0/1/2) zu
     * zusammenhaengenden Zustands-Segmenten { start, end, value } zusammen
     */
    function buildSegments (labels, data) {

      const segments = []
      for (let i = 0; i < data.length; i++) {

        let v = data[i]
        if (Object.prototype.hasOwnProperty.call(LEGACY_VALUE_MAP, String(v))) {
          v = LEGACY_VALUE_MAP[String(v)]
        }
        if (![0, 1, 2].includes(v)) {
          v = null // "keine Daten"
        }

        const ts = moment(labels[i], 'DD.MM.YYYY HH:mm').valueOf()
        const last = segments[segments.length - 1]
        if (last && last.value === v) {
          last.end = ts
        } else {
          segments.push({ start: ts, end: ts, value: v })
        }

      }

      // Ende jedes Segments = Start des naechsten Segments, letztes Segment +1 Minute
      for (let i = 0; i < segments.length; i++) {
        segments[i].end = i < segments.length - 1 ? segments[i + 1].start : segments[i].end + 60000
      }

      return segments

    }

    /**
     * zeigt an jedem Zustandswechsel die Uhrzeit oberhalb der Zeichenflaeche an
     * (die eigentliche Trennung der Segmente uebernimmt bereits der weisse
     * Balken-Rand, siehe borderWidth im Dataset - hier nur noch die Beschriftung)
     */
    const transitionLinesPlugin = {
      id: 'tuerSensorTransitionLines',
      afterDatasetsDraw (chart) {

        const meta = chart.getDatasetMeta(0)
        const data = chart.data && chart.data.datasets && chart.data.datasets[0] && chart.data.datasets[0].data
        if (!meta || !meta.data || !data || meta.data.length < 2) return

        const xScale = chart.scales.x
        const ctx = chart.ctx

        // ab Index 1: jeder Segmentanfang (ausser dem allerersten) ist ein Zustandswechsel
        const transitions = []
        for (let i = 1; i < data.length; i++) {
          const raw = data[i]
          if (!raw || !Array.isArray(raw.x)) continue
          transitions.push({ xPixel: xScale.getPixelForValue(raw.x[0]), timestamp: raw.x[0] })
        }

        // Uhrzeit an jedem Wechsel, senkrecht (analog zu den frueheren X-Achsen-
        // Ticks), mit Mindestabstand damit sich dicht beieinanderliegende Wechsel
        // nicht ueberlappen. Text waechst von einem festen Rand knapp unter dem
        // Canvas-Anfang NACH UNTEN zur Zeichenflaeche hin, damit er nicht am
        // oberen Canvas-Rand abgeschnitten wird (layout.padding.top reserviert
        // dafuer ausreichend Platz).
        ctx.save()
        ctx.fillStyle = '#495057'
        ctx.font = '10px sans-serif'
        ctx.textAlign = 'left'
        ctx.textBaseline = 'middle'
        const minSpacing = 14
        let lastLabelX = -Infinity
        transitions.forEach(t => {
          if (t.xPixel - lastLabelX < minSpacing) return
          lastLabelX = t.xPixel
          ctx.save()
          ctx.translate(t.xPixel + 3, 4)
          ctx.rotate(Math.PI / 2)
          ctx.fillText(moment(t.timestamp).format('HH:mm'), 0, 0)
          ctx.restore()
        })
        ctx.restore()

      }
    }

    export default {
      name: 'LineChartSensorResult',
      components: {
        HorizontalBarChart,
        SpinnerImage
      },
      props: [
        /** der Sensor */
        'value',
        'jahr',
        'monat',
        'woche',
        /** Tag ist ein moment Datum für Tagesansicht */
        'tag',
      ],
      data () {
        return {
          /** noch kein Ergebnis verfügbar - true setzen */
          setNoData: false,
          loaded: false,
          /** Daten für den Chart */
          chartdata: null,
          options: null,
          /** Chart.js Chart */
          chart: null,
          start: null,
          end: null,
          /** das Jahr für den die Chartdaten angezeigt werden sollen */
          year: null,
          /** das Monat für das die Chartdaten angezeigt werden sollen */
          month: null,
          /** der Tag als moment Objekt für das die Chartdaten angezeigt werden sollen */
          day: null,
          listImageGruen: require('@/assets/images/list-style-plan-gruen.gif'),
          listImageGelb:  require('@/assets/images/list-style-plan-gelb.gif'),
          listImageRot:   require('@/assets/images/list-style-plan-rot.gif'),
          /** X-Achse einblenden/ausblenden */
          showX: false,
          /** Chart.js-Plugins nur fuer diese Chart-Instanz (vertikale Trennlinien an Zustandswechseln) */
          chartPlugins: [transitionLinesPlugin],
        }
      },
      watch: {
        value: function () {
            this.init()
        },
        monat: function () {
          this.init()
        },
        tag: function () {
          this.init()
        },
        woche: function () {
          this.init()
        },
        showX: function (val) {

          console.log('showX', val)
          // Reference-Watch im Wrapper: ganzes options-Objekt neu zuweisen,
          // damit der Watcher feuert und chart.options + chart.update() laufen.
          if (this.options && this.options.scales && this.options.scales.x) {
            this.options = {
              ...this.options,
              scales: {
                ...this.options.scales,
                x: { ...this.options.scales.x, display: val },
              },
            }
          }

        }
      },
      created () {
        this.init()
      },
      methods: {

        /**
         * Chart des Parents zurückliefern um Klick Events zu behandeln
         */
        setChart: function (obj) {
          this.chart = obj
        },
        /** Beschriftung/Farbe eines Segment-Zustands */
        stateLabel: function (value) {

          if (value === 1) return this.$t('message.sensor_tuer_geschlossen')
          if (value === 0) return this.$t('message.sensor_tuer_offen_toleranz')
          if (value === 2) return this.$t('message.sensor_tuer_offen_schwellwert')
          return this.$t('message.sensor_tuer_keine_daten')

        },
        init: async function () {

          this.loaded = false
          if (this.value && this.value.id) {

            this.year = this.jahr
            if (!this.year) this.year = new Date().getFullYear()
            this.month = this.monat
            if (!this.month) this.month = new Date().getMonth() + 1

            const url     = process.env.VUE_APP_SERVER_URL + '/datenbroker/getBarChartSensor'
            const params  = {
              sensor_id:          this.value.id,
              year:               this.year,
              lang:               this.$i18n.locale,
              customer_id:        this.$store.getters.customer.id,
            }
            if (this.tag) {

              params.start = this.tag.format('DD.MM.YYYY')
              params.end   = this.tag.format('DD.MM.YYYY')

            } else if (this.woche) {

              const dat = moment().year(this.year).week(this.woche)
              const monday = dat.clone().weekday(0)
              const sunday = dat.clone().weekday(6)
              console.log('monday', monday)
              console.log('sunday', sunday)

              params.start = monday.format('DD.MM.YYYY')
              params.end   = sunday.format('DD.MM.YYYY')

            } else {
              params.month = this.month
            }

            this.chartdata = null
            const that = this
            await axios({
              method: 'GET',
              params: params,
              url: url,
            }).then(response => {

              console.log('response: ', response.data)

              if (response.data && response.data.chartdata) {

                const rawChartdata = response.data.chartdata
                const rawDataset   = rawChartdata.datasets && rawChartdata.datasets[0]

                if (!rawDataset || !Array.isArray(rawDataset.data) || rawDataset.data.length === 0) {

                  that.setNoData = true
                  that.chartdata = null

                } else {

                  that.setNoData = false

                  const segments = buildSegments(rawChartdata.labels || [], rawDataset.data)

                  that.chartdata = {
                    labels: ['Zustand'],
                    datasets: [{
                      label: that.$i18n.locale === 'de' ? 'Status' : 'State',
                      data: segments.map(s => ({ x: [s.start, s.end], y: 'Zustand' })),
                      backgroundColor: segments.map(s => STATE_COLORS[s.value] || NO_DATA_COLOR),
                      barThickness: 40,
                      // dunkler Rand links/rechts an jedem Segment - macht Zustandswechsel
                      // (z.B. GRUEN -> GELB/ROT) als klar sichtbare vertikale Trennlinie sichtbar
                      borderColor: '#ffffff',
                      borderWidth: { top: 0, bottom: 0, left: 1, right: 1 },
                      borderSkipped: false,
                    }],
                  }

                  const rangeStart = segments.length ? segments[0].start : null
                  const rangeEnd   = segments.length ? segments[segments.length - 1].end : null
                  const tickFormat = that.tag ? 'HH:mm' : 'DD.MM.'

                  that.options = {
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    // Platz oberhalb der Zeichenflaeche fuer die gedrehten Uhrzeit-Labels
                    // an den Trennlinien reservieren (transitionLinesPlugin)
                    layout: {
                      padding: { top: 42 },
                    },
                    interaction: {
                      mode: 'nearest',
                      intersect: true,
                      axis: 'x',
                    },
                    plugins: {
                      title: {
                        display: false,
                      },
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        callbacks: {
                          title: function () {
                            return ''
                          },
                          label: function (ctx) {
                            const [s, e] = ctx.raw.x
                            const dur = moment.duration(e - s)
                            const durationParts = []
                            if (dur.days())    durationParts.push(dur.days() + 'd')
                            if (dur.hours())   durationParts.push(dur.hours() + 'h')
                            durationParts.push(dur.minutes() + 'min')
                            return [
                              that.stateLabel(segments[ctx.dataIndex] ? segments[ctx.dataIndex].value : null),
                              moment(s).format('DD.MM.YYYY HH:mm') + ' – ' + moment(e).format('HH:mm'),
                              durationParts.join(' '),
                            ]
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        type: 'linear',
                        display: that.showX,
                        min: rangeStart,
                        max: rangeEnd,
                        grid: { display: false },
                        ticks: {
                          callback: function (val) {
                            return moment(val).format(tickFormat)
                          },
                        },
                      },
                      y: {
                        display: false,
                        stacked: true,
                      }
                    }
                  } // end options

                }

              } else {
                that.setNoData = true
                that.chartdata = null
              }

              that.loaded = true

            }).catch(error => {
              console.log(error)
              that.loaded = true
            })
          }

        },

      }

    }

</script>

<style scoped>
  /* eine einzelne horizontale Balkenzeile braucht deutlich weniger Hoehe als die
     vorherige Linien-Darstellung. Das innere div des HorizontalBarChart-Wrappers
     (position: relative) muss explizit auf 100% gesetzt werden, sonst nimmt es
     die Canvas-Default-Hoehe von 400px. */
  .sensorResultChart {
    position: relative;
    height: 90px;
  }
  .sensorResultChart > div {
    height: 100%;
  }
</style>
